import React, { useState, useContext, useEffect, useRef } from "react";
import { useDroppable, useDndContext } from "@dnd-kit/core";
import { IComponentNode } from "../../types/pageBuilder.types";
import { usePageBuilder } from "../../context/PageBuilderContext";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { FaTrash, FaEdit, FaImage, FaVideo, FaPlus } from "react-icons/fa";
import * as FaIcons from "react-icons/fa";
import * as Fa6Icons from "react-icons/fa6";
import {
  getOriginalImageUrl,
  getResizedImageUrl,
  ImagePresets,
} from "../../utils/imageUtils";
import { ModalLanguageContext } from "../../context/ModalLanguageContext";
import { SupportedLanguage } from "../../types/translations.types";
import { useTranslation } from "react-i18next";
import { useDynamicSnippetService } from "../../services/dynamicSnippetService";
import {
  DataSourceType,
  DisplayComponentType,
} from "../../types/pageBuilder.types";
import { StoreContext } from "../../context/StoreContext";
import { useCurrency } from "../../context/CurrencyContext";
import { useStoreLogoPlaceholder } from "../../hooks/useStoreLogoPlaceholder";
import { ProductCard } from "../ui/ProductCard";
import "react-quill/dist/quill.snow.css";
import { ensureFontsLoaded } from "../../utils/fontLoader";
import {
  createSanitizedFontMap,
  extractFontFamiliesFromHtml,
} from "../../utils/fontCatalog";

// Helper function to get translated value for IComponentNode
// IComponentNode stores values in props, not at root level
function getComponentTranslatedValue(
  component: IComponentNode,
  field: string,
  language: SupportedLanguage,
): string {
  // For English, use props directly
  if (language === "en") {
    return component.props[field] || "";
  }

  // For other languages, check translations
  const translations = component.translations?.[field];
  if (translations && translations[language]) {
    return translations[language] || "";
  }

  // Fallback to English (props)
  return component.props[field] || "";
}

const QUILL_SANITIZED_FONT_MAP = createSanitizedFontMap();

// Helper component to render translated text
const TranslatedText: React.FC<{
  component: IComponentNode;
  field: string;
  defaultText: string;
}> = ({ component, field, defaultText }) => {
  // Get language from ModalLanguageContext (for page builder) or i18n (for public view)
  const modalLanguageContext = useContext(ModalLanguageContext);
  const { i18n } = useTranslation();

  // Extract base language code (e.g., "en-US" -> "en", "de-DE" -> "de")
  let activeLanguage: string;
  if (modalLanguageContext?.currentLanguage) {
    // In page builder, use ModalLanguageContext
    activeLanguage = modalLanguageContext.currentLanguage;
  } else {
    // On public site, use i18n language
    const i18nLang = i18n.language || "en";
    activeLanguage = i18nLang.split("-")[0].toLowerCase();
  }

  const language: SupportedLanguage = (
    ["en", "de", "ar"].includes(activeLanguage) ? activeLanguage : "en"
  ) as SupportedLanguage;

  const textValue =
    getComponentTranslatedValue(component, field, language) || defaultText;
  return <>{textValue}</>;
};

interface ComponentRendererProps {
  component: IComponentNode;
  isPreviewMode?: boolean;
  onComponentClick?: (e: React.MouseEvent, component: IComponentNode) => void;
  depth?: number;
}

export const ComponentRenderer: React.FC<ComponentRendererProps> = ({
  component,
  isPreviewMode = false,
  onComponentClick,
  depth = 0,
}) => {
  const {
    selectedComponent,
    setSelectedComponent,
    deleteComponent,
    selectedDropTarget,
    setSelectedDropTarget,
  } = usePageBuilder();
  const { active } = useDndContext();
  const [isHovered, setIsHovered] = useState(false);

  // Dynamic snippet state and services
  const [dynamicSnippetData, setDynamicSnippetData] = useState<any[]>([]);
  const [loadingDynamicSnippet, setLoadingDynamicSnippet] = useState(false);
  const snippetService = useDynamicSnippetService();
  const storeLogoPlaceholder = useStoreLogoPlaceholder();
  const storeContext = useContext(StoreContext);
  const store = storeContext?.store || null;
  const { location } = useCurrency();
  const carouselRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  // Helper function to check if any child component is currently selected
  const hasSelectedChild = React.useCallback(
    (comp: IComponentNode): boolean => {
      if (!selectedComponent) return false;
      if (comp.children && comp.children.length > 0) {
        return comp.children.some((child) => {
          if (child.id === selectedComponent.id) return true;
          // Recursively check children
          const checkChildren = (c: IComponentNode): boolean => {
            if (c.id === selectedComponent.id) return true;
            if (c.children && c.children.length > 0) {
              return c.children.some((ch) => checkChildren(ch));
            }
            return false;
          };
          return checkChildren(child);
        });
      }
      return false;
    },
    [selectedComponent],
  );

  // Component is selected if it's the selected component
  // Allow selecting containers even when they have children - users can select any layer
  const isSelected = selectedComponent?.id === component.id;
  const isDragging = active?.id === component.id;
  const isDraggingSomething = !!active;

  // Only containers and grids can accept child elements
  // Basic elements like text, heading, image, button, link, etc. cannot accept children
  const canDrop = component.type === "container" || component.type === "grid";
  const { setNodeRef: setDroppableRef, isOver } = useDroppable({
    id: `droppable-${component.id}`,
    disabled: !canDrop || isPreviewMode,
  });

  const {
    attributes,
    listeners,
    setNodeRef: setSortableRef,
    transform,
    transition,
  } = useSortable({
    id: component.id,
    disabled: isPreviewMode,
  });

  // Modify listeners to exclude button clicks and prevent text selection
  const modifiedListeners = React.useMemo(() => {
    if (!listeners || isPreviewMode) return {};
    return {
      ...listeners,
      onPointerDown: (e: React.PointerEvent) => {
        // Don't start drag if clicking on action buttons
        const target = e.target as HTMLElement;
        if (target.closest(".absolute.-top-8") || target.closest("button")) {
          return;
        }
        // Prevent text selection during drag
        e.preventDefault();
        if (listeners.onPointerDown) {
          listeners.onPointerDown(e);
        }
      },
    };
  }, [listeners, isPreviewMode]);

  // Combine refs for sortable wrapper
  const nodeRef = useRef<HTMLElement | null>(null);
  const setNodeRef = React.useCallback(
    (node: HTMLElement | null) => {
      nodeRef.current = node;
      setSortableRef(node);
    },
    [setSortableRef],
  );

  // Load dynamic snippet data if component is a dynamic-snippet
  useEffect(() => {
    if (component.type === "dynamic-snippet" && component.props?.dataSource) {
      loadDynamicSnippetData();
    } else if (component.type === "dynamic-snippet") {
      // Reset data if dataSource is removed
      setDynamicSnippetData([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    component.type,
    component.props?.dataSource,
    component.props?.filters,
    component.props?.displayComponent,
    store?._id,
  ]);

  // Handle carousel scroll position checking
  useEffect(() => {
    if (
      component.type === "dynamic-snippet" &&
      component.props?.displayComponent === "carousel"
    ) {
      const checkScroll = () => {
        if (carouselRef.current) {
          const { scrollLeft, scrollWidth, clientWidth } = carouselRef.current;
          setCanScrollLeft(scrollLeft > 0);
          setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 1);
        }
      };

      checkScroll();
      const carousel = carouselRef.current;
      if (carousel) {
        carousel.addEventListener("scroll", checkScroll);
        // Also check on resize
        window.addEventListener("resize", checkScroll);
        return () => {
          carousel.removeEventListener("scroll", checkScroll);
          window.removeEventListener("resize", checkScroll);
        };
      }
    }
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [component.type, component.props?.displayComponent, dynamicSnippetData]);

  const loadDynamicSnippetData = async () => {
    if (!component.props?.dataSource) return;

    // For products and categories, we need a storeId
    const dataSource = component.props.dataSource as DataSourceType;
    if (
      (dataSource === "products" || dataSource === "categories") &&
      !store?._id
    ) {
      return;
    }

    try {
      setLoadingDynamicSnippet(true);
      const filters = component.props.filters || {};
      const limit = filters.limit || 12;

      // In page builder, always use the protected endpoint (preview mode)
      const result = await snippetService.executeDataSource(
        dataSource,
        filters,
        { limit },
      );
      setDynamicSnippetData(result.data);
    } catch (error) {
      console.error("Error loading dynamic snippet data:", error);
      setDynamicSnippetData([]);
    } finally {
      setLoadingDynamicSnippet(false);
    }
  };

  // Handle selectstart event to prevent text selection in non-editable areas
  useEffect(() => {
    const node = nodeRef.current;
    if (!node || isPreviewMode) return;

    const handleSelectStart = (e: Event) => {
      const target = e.target;
      // Check if target is an HTMLElement (not a text node or other node type)
      if (!(target instanceof HTMLElement)) {
        // If it's not an HTMLElement, try to get the parent element
        const element = (target as Node).parentElement;
        if (!element) {
          // If no parent element, prevent selection to be safe
          e.preventDefault();
          return;
        }
        // Check if parent is editable content
        const isEditableContent =
          element.closest(".ql-editor") ||
          element.closest('[contenteditable="true"]') ||
          element.closest("input") ||
          element.closest("textarea") ||
          element.closest(".select-text");
        if (!isEditableContent) {
          e.preventDefault();
        }
        return;
      }

      // Target is an HTMLElement, safe to use closest
      const isEditableContent =
        target.closest(".ql-editor") ||
        target.closest('[contenteditable="true"]') ||
        target.closest("input") ||
        target.closest("textarea") ||
        target.closest(".select-text");
      if (!isEditableContent) {
        e.preventDefault();
      }
    };

    node.addEventListener("selectstart", handleSelectStart);
    return () => {
      node.removeEventListener("selectstart", handleSelectStart);
    };
  }, [isPreviewMode]);

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const handleClick = (e: React.MouseEvent) => {
    // Don't select if clicking on action buttons or nested components
    const target = e.target as HTMLElement;
    if (target.closest(".absolute.top-0.right-0") || target.closest("button")) {
      return;
    }

    // Don't select container if clicking on an icon component
    if (target.closest("span[data-icon-component]")) {
      return;
    }

    // Don't select container if a child component is already selected
    if (hasSelectedChild(component)) {
      return;
    }

    // Select component on click to show edit/delete icons
    // But don't open the drawer - that's only via edit icon or double click
    e.preventDefault();
    e.stopPropagation();
    // Prevent text selection
    if (window.getSelection) {
      window.getSelection()?.removeAllRanges();
    }
    if (!isPreviewMode) {
      setSelectedComponent(component);
    }
    if (onComponentClick) {
      onComponentClick(e, component);
    }
  };

  const handleDoubleClick = (e: React.MouseEvent) => {
    // Don't open drawer if clicking on action buttons
    const target = e.target as HTMLElement;
    if (target.closest(".absolute.top-0.right-0") || target.closest("button")) {
      return;
    }

    // Don't open drawer if clicking on an icon component
    if (target.closest("span[data-icon-component]")) {
      return;
    }

    // Don't open drawer if a child component is already selected
    if (hasSelectedChild(component)) {
      return;
    }

    e.preventDefault();
    e.stopPropagation();
    // Prevent text selection on double-click
    if (window.getSelection) {
      window.getSelection()?.removeAllRanges();
    }
    if (!isPreviewMode) {
      setSelectedComponent(component);
      // Trigger drawer to open on double click
      window.dispatchEvent(new CustomEvent("pagebuilder:openDrawer"));
    }
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    deleteComponent(component.id);
  };

  const handlePlusClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setSelectedDropTarget(component.id);
  };

  const isSelectedAsDropTarget = selectedDropTarget === component.id;

  // Get component display name
  const getComponentLabel = (): string => {
    const typeLabels: Record<string, string> = {
      container: "Container",
      heading: "Heading",
      text: "Text",
      image: "Image",
      button: "Button",
      link: "Link",
      grid: "Grid",
      list: "List",
      "ordered-list": "Ordered List",
      quote: "Quote",
      divider: "Divider",
      code: "Code Block",
      card: "Card",
      carousel: "Carousel",
      spacer: "Spacer",
      video: "Video",
    };
    return (
      typeLabels[component.type] ||
      component.type.charAt(0).toUpperCase() + component.type.slice(1)
    );
  };

  // Extract positioning-related styles to apply to wrapper
  const getPositioningStyles = (): React.CSSProperties => {
    const defaultStyles = component.styles?.default || {};
    const positioningStyles: React.CSSProperties = {};

    if (defaultStyles.position)
      positioningStyles.position = defaultStyles.position as any;
    if (defaultStyles.top !== undefined)
      positioningStyles.top = defaultStyles.top;
    if (defaultStyles.right !== undefined)
      positioningStyles.right = defaultStyles.right;
    if (defaultStyles.bottom !== undefined)
      positioningStyles.bottom = defaultStyles.bottom;
    if (defaultStyles.left !== undefined)
      positioningStyles.left = defaultStyles.left;
    if (defaultStyles.zIndex !== undefined)
      positioningStyles.zIndex = defaultStyles.zIndex;

    return positioningStyles;
  };

  // Get non-positioning styles for inner content
  const getContentStyles = (): React.CSSProperties => {
    const defaultStyles = component.styles?.default || {};
    const contentStyles: React.CSSProperties = { ...defaultStyles };

    // Remove positioning styles from content styles (they go on wrapper)
    delete contentStyles.position;
    delete contentStyles.top;
    delete contentStyles.right;
    delete contentStyles.bottom;
    delete contentStyles.left;
    delete contentStyles.zIndex;

    // For dynamic snippets with content, remove editor-specific border and background styles
    // These are placeholder styles that shouldn't be applied when content is displayed
    if (
      component.type === "dynamic-snippet" &&
      component.props?.dataSource &&
      dynamicSnippetData.length > 0 &&
      !loadingDynamicSnippet
    ) {
      delete contentStyles.border;
      delete contentStyles.borderWidth;
      delete contentStyles.borderStyle;
      delete contentStyles.borderColor;
      delete contentStyles.backgroundColor;
      delete contentStyles.background;
      // Also remove placeholder-specific styles
      delete contentStyles.padding;
      delete contentStyles.minHeight;
      delete contentStyles.display;
      delete contentStyles.alignItems;
      delete contentStyles.justifyContent;
      delete contentStyles.flexDirection;
    }

    return contentStyles;
  };

  // Get language for translations at component level (not inside render function)
  const modalLanguageContext = useContext(ModalLanguageContext);
  const { i18n } = useTranslation();

  // Extract base language code (e.g., "en-US" -> "en", "de-DE" -> "de")
  let activeLanguage: string;
  if (modalLanguageContext?.currentLanguage) {
    // In page builder, use ModalLanguageContext
    activeLanguage = modalLanguageContext.currentLanguage;
  } else {
    // On public site, use i18n language
    const i18nLang = i18n.language || "en";
    activeLanguage = i18nLang.split("-")[0].toLowerCase();
  }

  const componentLanguage: SupportedLanguage = (
    ["en", "de", "ar"].includes(activeLanguage) ? activeLanguage : "en"
  ) as SupportedLanguage;

  // Load any fonts referenced by Quill HTML in "text" components (on demand)
  useEffect(() => {
    if (component.type !== "text") return;

    const html =
      getComponentTranslatedValue(component, "text", componentLanguage) || "";
    if (!html) return;
    if (!/<[a-z][\s\S]*>/i.test(html)) return; // only bother for HTML content

    const fontFamilies = extractFontFamiliesFromHtml(
      html,
      QUILL_SANITIZED_FONT_MAP,
    );
    if (fontFamilies.length === 0) return;
    ensureFontsLoaded(fontFamilies).catch(() => {
      // Non-fatal; fallback fonts will render
    });
  }, [component, componentLanguage]);

  const renderComponentContent = () => {
    const componentClassName = component.props?.className || undefined;

    switch (component.type) {
      case "container":
        const containerStyles = getContentStyles();
        // Container should be relative if it has relative positioning
        // This creates a positioning context for absolutely positioned children
        const containerIsRelative =
          component.styles?.default?.position === "relative";
        // If container has href, render as link
        if (component.props?.href) {
          return (
            <a
              href={component.props.href}
              ref={setDroppableRef}
              style={containerStyles}
              className={`min-h-[100px] transition-all duration-200 block ${
                containerIsRelative ? "relative" : ""
              } ${
                isSelectedAsDropTarget
                  ? "ring-2 ring-green-500 bg-green-100/50 "
                  : isOver
                    ? "ring-2 ring-blue-500 bg-blue-100/50 border-2 border-dashed border-blue-400"
                    : isDraggingSomething && canDrop
                      ? "bg-gray-50/50 opacity-60 border-2 border-dashed border-gray-300"
                      : ""
              } ${componentClassName || ""}`}
            >
              {component.children && component.children.length > 0 ? (
                component.children.map((child) => (
                  <ComponentRenderer
                    key={child.id}
                    component={child}
                    isPreviewMode={isPreviewMode}
                    onComponentClick={onComponentClick}
                    depth={depth + 1}
                  />
                ))
              ) : (
                <div
                  className={`py-8 text-center text-sm transition-colors relative ${
                    isOver
                      ? "bg-blue-100 text-blue-700 font-medium border-2 border-dashed border-blue-400 rounded"
                      : isDraggingSomething && canDrop
                        ? "text-gray-500 border-2 border-dashed border-gray-300 rounded opacity-50"
                        : "text-gray-400"
                  }`}
                >
                  {!isPreviewMode && (
                    <button
                      type="button"
                      onClick={handlePlusClick}
                      className={`absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-blue-500 text-white p-2 hover:bg-blue-600 transition-colors z-10 ${
                        isSelectedAsDropTarget
                          ? "ring-2 ring-green-500 ring-offset-2"
                          : ""
                      }`}
                      title="Click to select as drop target, then click a component from palette"
                    >
                      <FaPlus className="text-lg" />
                    </button>
                  )}
                  <div className={isSelectedAsDropTarget ? "mt-12" : ""}>
                    {isSelectedAsDropTarget ? (
                      <span className="text-green-700 font-medium">
                        Selected - Click a component from palette
                      </span>
                    ) : (
                      "Drop components here or click + to select"
                    )}
                  </div>
                </div>
              )}
            </a>
          );
        }
        return (
          <div
            ref={setDroppableRef}
            style={containerStyles}
            className={`min-h-[100px] transition-all duration-200 ${
              containerIsRelative ? "relative" : ""
            } ${
              isSelectedAsDropTarget
                ? "ring-2 ring-green-500 bg-green-100/50 "
                : isOver
                  ? "ring-2 ring-blue-500 bg-blue-100/50 "
                  : isDraggingSomething && canDrop
                    ? "bg-gray-50/50 opacity-60"
                    : ""
            } ${componentClassName || ""}`}
          >
            {component.children && component.children.length > 0 ? (
              component.children.map((child) => (
                <ComponentRenderer
                  key={child.id}
                  component={child}
                  isPreviewMode={isPreviewMode}
                  onComponentClick={onComponentClick}
                  depth={depth + 1}
                />
              ))
            ) : (
              <div
                className={`py-8 text-center text-sm transition-colors relative ${
                  isOver
                    ? "bg-blue-100 text-blue-700 font-medium"
                    : isDraggingSomething && canDrop
                      ? "text-gray-500"
                      : "text-gray-400"
                }`}
              >
                {!isPreviewMode && (
                  <button
                    type="button"
                    onClick={handlePlusClick}
                    className={`absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-blue-500 text-white p-2 hover:bg-blue-600 transition-colors z-10 ${
                      isSelectedAsDropTarget
                        ? "ring-2 ring-green-500 ring-offset-2"
                        : ""
                    }`}
                    title="Click to select as drop target, then click a component from palette"
                  >
                    <FaPlus className="text-lg" />
                  </button>
                )}
                <div className={isSelectedAsDropTarget ? "mt-12" : ""}>
                  {isSelectedAsDropTarget ? (
                    <span className="text-green-700 font-medium">
                      Selected - Click a component from palette
                    </span>
                  ) : (
                    "Drop components here or click + to select"
                  )}
                </div>
              </div>
            )}
          </div>
        );
      case "heading":
        const HeadingTag =
          `h${component.props.level || 1}` as keyof JSX.IntrinsicElements;
        return (
          <HeadingTag
            style={getContentStyles()}
            {...(componentClassName && { className: componentClassName })}
          >
            <TranslatedText
              component={component}
              field="text"
              defaultText="Heading Text"
            />
          </HeadingTag>
        );
      case "text":
        // For text components, we need to get the translated value here since we need it for hasText check
        // We'll create a sub-component to handle the translation
        const TextContent: React.FC = () => {
          // Use the component-level language that was already detected
          const textValue =
            getComponentTranslatedValue(component, "text", componentLanguage) ||
            "";
          const hasText = textValue && textValue.trim() !== "";
          const containsHTML = hasText && /<[a-z][\s\S]*>/i.test(textValue);

          if (!hasText) {
            return !isPreviewMode ? (
              <span className="text-gray-400 italic">Enter text here</span>
            ) : null;
          }

          return (
            <div
              className={containsHTML ? "ql-editor" : ""}
              dangerouslySetInnerHTML={{ __html: textValue }}
            />
          );
        };

        const textValueForCheck = component.props.text || "";
        const hasTextForCheck =
          textValueForCheck && textValueForCheck.trim() !== "";
        const containsHTMLForCheck =
          hasTextForCheck && /<[a-z][\s\S]*>/i.test(textValueForCheck);

        return (
          <div
            style={getContentStyles()}
            className={`${!hasTextForCheck && !isPreviewMode ? "text-gray-400 italic" : ""} ${containsHTMLForCheck ? "ql-snow" : ""} ${componentClassName || ""}`}
          >
            <div className={containsHTMLForCheck ? "select-text" : ""}>
              <TextContent />
            </div>
          </div>
        );
      case "image":
        // Get translated image src if available
        let imageSrc = component.props.src || "";
        if (componentLanguage !== "en") {
          const translations = component.translations?.src;
          if (translations && translations[componentLanguage]) {
            imageSrc = translations[componentLanguage];
          }
        }
        const hasImage = imageSrc && imageSrc.trim() !== "";
        const imageUrl =
          hasImage && !imageSrc.startsWith("http") && !imageSrc.startsWith("/")
            ? getOriginalImageUrl(imageSrc)
            : imageSrc;
        const imageContentStyles = getContentStyles();
        const finalImageStyles: React.CSSProperties = {
          ...imageContentStyles,
          objectFit:
            component.props.objectFit ||
            imageContentStyles.objectFit ||
            "cover",
        };
        return (
          <div
            style={imageContentStyles}
            className={`${component.styles?.default?.position === "relative" ? "relative" : ""} ${componentClassName || ""}`}
          >
            {hasImage ? (
              <img
                src={imageUrl}
                alt={component.props.alt || ""}
                style={finalImageStyles}
                {...(componentClassName && { className: componentClassName })}
                loading={
                  component.props.loading === "lazy" ||
                  component.props.loading === true
                    ? "lazy"
                    : "eager"
                }
              />
            ) : (
              <div className="w-full h-48 bg-gray-100 border-2 border-dashed border-gray-300 flex items-center justify-center text-gray-400">
                <div className="text-center">
                  <FaImage className="mx-auto mb-2 text-2xl" />
                  <p className="text-sm">Click edit icon to add image</p>
                </div>
              </div>
            )}
          </div>
        );
      case "button":
        return (
          <button
            style={getContentStyles()}
            className={`${!component.props.text && !isPreviewMode ? "opacity-50" : ""} ${componentClassName || ""}`}
          >
            <TranslatedText
              component={component}
              field="text"
              defaultText={!isPreviewMode ? "Button" : ""}
            />
          </button>
        );
      case "link":
        return (
          <a
            href={component.props.href || "#"}
            style={getContentStyles()}
            className={`${!component.props.text && !isPreviewMode ? "text-gray-400 italic" : ""} ${componentClassName || ""}`}
          >
            <TranslatedText
              component={component}
              field="text"
              defaultText={!isPreviewMode ? "Link Text" : ""}
            />
          </a>
        );
      case "list":
        return (
          <ul
            style={getContentStyles()}
            {...(componentClassName && { className: componentClassName })}
          >
            {(component.props.items || []).length > 0
              ? (component.props.items || []).map(
                  (item: string, index: number) => <li key={index}>{item}</li>,
                )
              : !isPreviewMode && (
                  <li className="text-gray-400 italic">List item</li>
                )}
          </ul>
        );
      case "ordered-list":
        return (
          <ol
            style={getContentStyles()}
            {...(componentClassName && { className: componentClassName })}
          >
            {(component.props.items || []).length > 0
              ? (component.props.items || []).map(
                  (item: string, index: number) => <li key={index}>{item}</li>,
                )
              : !isPreviewMode && (
                  <li className="text-gray-400 italic">List item</li>
                )}
          </ol>
        );
      case "quote":
        return (
          <blockquote
            style={getContentStyles()}
            {...(componentClassName && { className: componentClassName })}
          >
            <p>
              {component.props.text || (!isPreviewMode ? "Quote text" : "")}
            </p>
            {component.props.author && <cite>— {component.props.author}</cite>}
          </blockquote>
        );
      case "divider":
        return (
          <hr
            style={getContentStyles()}
            {...(componentClassName && { className: componentClassName })}
          />
        );
      case "code":
        return (
          <pre
            style={getContentStyles()}
            {...(componentClassName && { className: componentClassName })}
          >
            <code>
              {component.props.code ||
                (!isPreviewMode ? "// Your code here" : "")}
            </code>
          </pre>
        );
      case "card":
        return (
          <div
            style={getContentStyles()}
            {...(componentClassName && { className: componentClassName })}
          >
            {component.props.title && (
              <h3 className="text-xl font-semibold mb-2">
                {component.props.title}
              </h3>
            )}
            <p>
              {component.props.content ||
                (!isPreviewMode ? "Card content goes here" : "")}
            </p>
          </div>
        );
      case "grid":
        const columns = component.props?.columns ?? 3;
        const rows = component.props?.rows;
        const gap = component.props?.gap ?? "1rem";
        const gridContentStyles = getContentStyles();
        const gridStyle: React.CSSProperties = {
          ...gridContentStyles,
          display: "grid",
          gridTemplateColumns: `repeat(${columns}, 1fr)`,
          gap: gap,
        };
        if (rows && rows > 0) {
          gridStyle.gridTemplateRows = `repeat(${rows}, 1fr)`;
        }
        return (
          <div
            ref={setDroppableRef}
            style={gridStyle}
            className={`min-h-[100px] transition-all duration-200 relative ${
              isSelectedAsDropTarget
                ? "ring-2 ring-green-500 bg-green-100/50 "
                : isOver
                  ? "ring-2 ring-blue-500 bg-blue-100/50 "
                  : isDraggingSomething && canDrop
                    ? "bg-gray-50/50 opacity-60"
                    : ""
            } ${componentClassName || ""}`}
          >
            {component.children && component.children.length > 0 ? (
              component.children.map((child) => (
                <ComponentRenderer
                  key={child.id}
                  component={child}
                  isPreviewMode={isPreviewMode}
                  onComponentClick={onComponentClick}
                  depth={depth + 1}
                />
              ))
            ) : (
              <div
                className={`py-8 text-center text-sm transition-colors relative ${
                  isOver
                    ? "bg-blue-100 text-blue-700 font-medium"
                    : isDraggingSomething && canDrop
                      ? "text-gray-500"
                      : "text-gray-400"
                }`}
              >
                {!isPreviewMode && (
                  <button
                    type="button"
                    onClick={handlePlusClick}
                    className={`absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-blue-500 text-white p-2 hover:bg-blue-600 transition-colors z-10 ${
                      isSelectedAsDropTarget
                        ? "ring-2 ring-green-500 ring-offset-2"
                        : ""
                    }`}
                    title="Click to select as drop target, then click a component from palette"
                  >
                    <FaPlus className="text-lg" />
                  </button>
                )}
                <div className={isSelectedAsDropTarget ? "mt-12" : ""}>
                  {isSelectedAsDropTarget ? (
                    <span className="text-green-700 font-medium">
                      Selected - Click a component from palette
                    </span>
                  ) : (
                    "Add grid items or click + to select"
                  )}
                </div>
              </div>
            )}
          </div>
        );
      case "carousel":
        return (
          <div
            ref={setDroppableRef}
            style={getContentStyles()}
            className={`min-h-[100px] transition-all duration-200 relative ${
              isSelectedAsDropTarget
                ? "ring-2 ring-green-500 bg-green-100/50 "
                : isOver
                  ? "ring-2 ring-blue-500 bg-blue-100/50 "
                  : isDraggingSomething && canDrop
                    ? "bg-gray-50/50 opacity-60"
                    : ""
            } ${componentClassName || ""}`}
          >
            {component.children && component.children.length > 0 ? (
              component.children.map((child) => (
                <ComponentRenderer
                  key={child.id}
                  component={child}
                  isPreviewMode={isPreviewMode}
                  onComponentClick={onComponentClick}
                  depth={depth + 1}
                />
              ))
            ) : (
              <div
                className={`py-8 text-center text-sm transition-colors relative ${
                  isOver
                    ? "bg-blue-100 text-blue-700 font-medium"
                    : isDraggingSomething && canDrop
                      ? "text-gray-500"
                      : "text-gray-400"
                }`}
              >
                {!isPreviewMode && (
                  <button
                    type="button"
                    onClick={handlePlusClick}
                    className={`absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-blue-500 text-white p-2 hover:bg-blue-600 transition-colors z-10 ${
                      isSelectedAsDropTarget
                        ? "ring-2 ring-green-500 ring-offset-2"
                        : ""
                    }`}
                    title="Click to select as drop target, then click a component from palette"
                  >
                    <FaPlus className="text-lg" />
                  </button>
                )}
                <div className={isSelectedAsDropTarget ? "mt-12" : ""}>
                  {isSelectedAsDropTarget ? (
                    <span className="text-green-700 font-medium">
                      Selected - Click a component from palette
                    </span>
                  ) : (
                    "Add carousel items or click + to select"
                  )}
                </div>
              </div>
            )}
          </div>
        );
      case "spacer":
        return (
          <div
            style={getContentStyles()}
            {...(componentClassName && { className: componentClassName })}
          />
        );
      case "video":
        const hasVideo =
          component.props.src && component.props.src.trim() !== "";
        return (
          <div
            style={getContentStyles()}
            className={`${component.styles?.default?.position === "relative" ? "relative" : ""} ${componentClassName || ""}`}
          >
            {hasVideo ? (
              <video
                src={component.props.src}
                controls={component.props.controls}
                autoPlay={component.props.autoplay}
                className={`w-full ${componentClassName || ""}`}
              />
            ) : (
              <div className="w-full h-64 bg-gray-100 border-2 border-dashed border-gray-300 flex items-center justify-center text-gray-400">
                <div className="text-center">
                  <FaVideo className="mx-auto mb-2 text-2xl" />
                  <p className="text-sm">Click edit icon to add video</p>
                </div>
              </div>
            )}
          </div>
        );
      case "icon": {
        const iconName = component.props.iconName || "FaCheck";
        const iconLibrary = component.props.iconLibrary || "fa";
        const iconColor = component.props.color || "#4F46E5";
        const iconSize = parseInt(component.props.size || "24", 10);

        // Get icon from appropriate library
        const IconLibrary = iconLibrary === "fa6" ? Fa6Icons : FaIcons;
        const IconComponent = (IconLibrary as any)[iconName] || FaIcons.FaCheck;

        return (
          <span
            data-icon-component="true"
            data-component-id={component.id}
            style={{
              ...getContentStyles(),
              color: iconColor,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
            }}
            {...(componentClassName && { className: componentClassName })}
            onClick={(e: React.MouseEvent) => {
              // Prevent parent container from being selected when clicking on icon
              e.preventDefault();
              e.stopPropagation();
              e.nativeEvent.stopImmediatePropagation();
              if (!isPreviewMode) {
                // Select icon immediately
                setSelectedComponent(component);
              }
            }}
            onMouseDown={(e: React.MouseEvent) => {
              // Also prevent on mousedown to catch drag events and prevent container selection
              e.preventDefault();
              e.stopPropagation();
              e.nativeEvent.stopImmediatePropagation();
              if (!isPreviewMode) {
                // Select icon on mousedown as well to ensure it's selected before container
                setSelectedComponent(component);
              }
            }}
            onPointerDown={(e: React.PointerEvent) => {
              // Prevent pointer events from bubbling to container
              e.preventDefault();
              e.stopPropagation();
              e.nativeEvent.stopImmediatePropagation();
            }}
            className={`${componentClassName || ""} ${!isPreviewMode ? "cursor-pointer" : ""}`}
          >
            <IconComponent
              size={iconSize}
              style={{ color: iconColor, pointerEvents: "none" }}
              onClick={(e: React.MouseEvent) => {
                // Prevent icon SVG from triggering events
                e.stopPropagation();
              }}
            />
          </span>
        );
      }
      case "dynamic-snippet": {
        const displayComponent =
          (component.props?.displayComponent as DisplayComponentType) || "grid";
        const hasDataSource = component.props?.dataSource;

        // Show placeholder if no data source is configured
        if (!hasDataSource) {
          return (
            <div style={getContentStyles()} className={componentClassName}>
              <div
                style={{
                  minHeight: "200px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexDirection: "column",
                  gap: "0.5rem",
                  padding: "2rem",
                  border: "2px dashed #d1d5db",
                  borderRadius: "0.5rem",
                  backgroundColor: "#f9fafb",
                }}
              >
                <div style={{ fontSize: "3rem", opacity: 0.3 }}>📊</div>
                <p
                  style={{
                    color: "#6b7280",
                    fontSize: "0.875rem",
                    textAlign: "center",
                  }}
                >
                  {component.props?.placeholder ||
                    "Configure dynamic content in the Data tab"}
                </p>
              </div>
            </div>
          );
        }

        // Show loading state
        if (loadingDynamicSnippet) {
          return (
            <div style={getContentStyles()} className={componentClassName}>
              <div
                style={{
                  minHeight: "200px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <p style={{ color: "#6b7280" }}>Loading...</p>
              </div>
            </div>
          );
        }

        // Show empty state
        if (dynamicSnippetData.length === 0 && !loadingDynamicSnippet) {
          return (
            <div style={getContentStyles()} className={componentClassName}>
              <div
                style={{
                  minHeight: "200px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexDirection: "column",
                  gap: "0.5rem",
                }}
              >
                <p style={{ color: "#6b7280" }}>No data available</p>
                {!store?._id && (
                  <p style={{ color: "#9ca3af", fontSize: "0.75rem" }}>
                    Store context required
                  </p>
                )}
              </div>
            </div>
          );
        }

        const dataSource = component.props.dataSource as DataSourceType;

        // Render based on display component type
        switch (displayComponent) {
          case "grid": {
            const columns = component.props?.columns || {
              mobile: 1,
              tablet: 2,
              desktop: 3,
            };
            const contentStyles = getContentStyles();
            const gridStyle: React.CSSProperties = {
              display: "grid",
              gap: component.props?.gap || "1rem",
              gridTemplateColumns: `repeat(${columns.desktop || 3}, 1fr)`,
              width: "100%",
              ...contentStyles,
            };

            return (
              <div style={gridStyle} className={componentClassName}>
                {dynamicSnippetData.map((item, index) => {
                  if (dataSource === "products") {
                    const image =
                      item.images?.find((img: any) => img.isPrimary) ||
                      item.images?.[0];
                    const imageUrl = image
                      ? getResizedImageUrl(
                          image.url,
                          ImagePresets.productCard,
                          storeLogoPlaceholder,
                        )
                      : storeLogoPlaceholder;
                    const locationId = location?.locationId;

                    return (
                      <div
                        key={item._id || index}
                        style={{ minWidth: 0, width: "100%" }}
                      >
                        <ProductCard
                          id={item._id}
                          title={item.title}
                          image={imageUrl}
                          currency={location?.currency || "USD"}
                          locationId={locationId}
                          href={`/products/${item.handle || item._id}`}
                        />
                      </div>
                    );
                  } else if (dataSource === "categories") {
                    return (
                      <div
                        key={item._id || index}
                        style={{
                          padding: "1rem",
                          border: "1px solid #e5e7eb",
                          borderRadius: "0.5rem",
                          minWidth: 0,
                          width: "100%",
                        }}
                      >
                        <h3
                          style={{
                            fontSize: "1.25rem",
                            fontWeight: "bold",
                            marginBottom: "0.5rem",
                          }}
                        >
                          {item.name}
                        </h3>
                        {item.description && (
                          <p style={{ color: "#6b7280", fontSize: "0.875rem" }}>
                            {item.description}
                          </p>
                        )}
                      </div>
                    );
                  }
                  return null;
                })}
              </div>
            );
          }
          case "carousel": {
            // Scroll handlers
            const scrollLeft = () => {
              if (carouselRef.current) {
                const cardWidth = 300 + 16; // minWidth + gap
                carouselRef.current.scrollBy({
                  left: -cardWidth,
                  behavior: "smooth",
                });
              }
            };

            const scrollRight = () => {
              if (carouselRef.current) {
                const cardWidth = 300 + 16; // minWidth + gap
                carouselRef.current.scrollBy({
                  left: cardWidth,
                  behavior: "smooth",
                });
              }
            };

            return (
              <div
                style={{ ...getContentStyles(), position: "relative" }}
                className={componentClassName}
              >
                {canScrollLeft && (
                  <button
                    onClick={scrollLeft}
                    className="absolute left-2 top-1/2 -translate-y-1/2 z-10 bg-white/90 hover:bg-white shadow-lg rounded-full p-2 transition-all"
                    aria-label="Previous"
                  >
                    <FaIcons.FaChevronLeft className="text-gray-700" />
                  </button>
                )}
                <div
                  ref={carouselRef}
                  style={{
                    display: "flex",
                    overflowX: "auto",
                    gap: "1rem",
                    scrollSnapType: "x mandatory",
                    scrollbarWidth: "none",
                    msOverflowStyle: "none",
                  }}
                  className="[&::-webkit-scrollbar]:hidden"
                >
                  {dynamicSnippetData.map((item, index) => {
                    if (dataSource === "products") {
                      const image =
                        item.images?.find((img: any) => img.isPrimary) ||
                        item.images?.[0];
                      const imageUrl = image
                        ? getResizedImageUrl(
                            image.url,
                            ImagePresets.productCard,
                            storeLogoPlaceholder,
                          )
                        : storeLogoPlaceholder;
                      const locationId = location?.locationId;

                      return (
                        <div
                          key={item._id || index}
                          style={{
                            minWidth: "300px",
                            scrollSnapAlign: "start",
                          }}
                        >
                          <ProductCard
                            id={item._id}
                            title={item.title}
                            image={imageUrl}
                            currency={location?.currency || "USD"}
                            locationId={locationId}
                            product={item}
                            href={`/products/${item.handle || item._id}`}
                          />
                        </div>
                      );
                    } else if (dataSource === "categories") {
                      return (
                        <div
                          key={item._id || index}
                          style={{
                            minWidth: "300px",
                            padding: "1rem",
                            border: "1px solid #e5e7eb",
                            borderRadius: "0.5rem",
                            scrollSnapAlign: "start",
                          }}
                        >
                          <h3
                            style={{
                              fontSize: "1.25rem",
                              fontWeight: "bold",
                              marginBottom: "0.5rem",
                            }}
                          >
                            {item.name}
                          </h3>
                          {item.description && (
                            <p
                              style={{ color: "#6b7280", fontSize: "0.875rem" }}
                            >
                              {item.description}
                            </p>
                          )}
                        </div>
                      );
                    }
                    return null;
                  })}
                </div>
                {canScrollRight && (
                  <button
                    onClick={scrollRight}
                    className="absolute right-2 top-1/2 -translate-y-1/2 z-10 bg-white/90 hover:bg-white shadow-lg rounded-full p-2 transition-all"
                    aria-label="Next"
                  >
                    <FaIcons.FaChevronRight className="text-gray-700" />
                  </button>
                )}
              </div>
            );
          }
          case "list": {
            return (
              <div
                style={{
                  ...getContentStyles(),
                  display: "flex",
                  flexDirection: "column",
                  gap: "1rem",
                }}
                className={componentClassName}
              >
                {dynamicSnippetData.map((item, index) => {
                  if (dataSource === "products") {
                    const image =
                      item.images?.find((img: any) => img.isPrimary) ||
                      item.images?.[0];
                    const imageUrl = image
                      ? getResizedImageUrl(
                          image.url,
                          ImagePresets.productCard,
                          storeLogoPlaceholder,
                        )
                      : storeLogoPlaceholder;
                    const locationId = location?.locationId;

                    return (
                      <div
                        key={item._id || index}
                        style={{
                          display: "flex",
                          gap: "1rem",
                          padding: "1rem",
                          border: "1px solid #e5e7eb",
                          borderRadius: "0.5rem",
                        }}
                      >
                        <img
                          src={imageUrl}
                          alt={item.title}
                          style={{
                            width: "100px",
                            height: "100px",
                            objectFit: "cover",
                            borderRadius: "0.25rem",
                          }}
                        />
                        <div style={{ flex: 1 }}>
                          <h3
                            style={{
                              fontSize: "1.125rem",
                              fontWeight: "bold",
                              marginBottom: "0.5rem",
                            }}
                          >
                            {item.title}
                          </h3>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "0.5rem",
                            }}
                          >
                            <ProductCard
                              id={item._id}
                              title={item.title}
                              image={imageUrl}
                              currency={location?.currency || "USD"}
                              locationId={locationId}
                              product={item}
                              href={`/products/${item.handle || item._id}`}
                            />
                          </div>
                        </div>
                      </div>
                    );
                  } else if (dataSource === "categories") {
                    return (
                      <div
                        key={item._id || index}
                        style={{
                          padding: "1rem",
                          border: "1px solid #e5e7eb",
                          borderRadius: "0.5rem",
                        }}
                      >
                        <h3
                          style={{
                            fontSize: "1.125rem",
                            fontWeight: "bold",
                            marginBottom: "0.5rem",
                          }}
                        >
                          {item.name}
                        </h3>
                        {item.description && (
                          <p style={{ color: "#6b7280", fontSize: "0.875rem" }}>
                            {item.description}
                          </p>
                        )}
                      </div>
                    );
                  }
                  return null;
                })}
              </div>
            );
          }
          case "card": {
            return (
              <div
                style={{
                  ...getContentStyles(),
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
                  gap: "1rem",
                }}
                className={componentClassName}
              >
                {dynamicSnippetData.map((item, index) => {
                  if (dataSource === "products") {
                    const image =
                      item.images?.find((img: any) => img.isPrimary) ||
                      item.images?.[0];
                    const imageUrl = image
                      ? getResizedImageUrl(
                          image.url,
                          ImagePresets.productCard,
                          storeLogoPlaceholder,
                        )
                      : storeLogoPlaceholder;
                    const locationId = location?.locationId;

                    return (
                      <div
                        key={item._id || index}
                        style={{
                          border: "1px solid #e5e7eb",
                          borderRadius: "0.5rem",
                          overflow: "hidden",
                        }}
                      >
                        <ProductCard
                          id={item._id}
                          title={item.title}
                          image={imageUrl}
                          currency={location?.currency || "USD"}
                          locationId={locationId}
                          href={`/products/${item.handle || item._id}`}
                        />
                      </div>
                    );
                  } else if (dataSource === "categories") {
                    return (
                      <div
                        key={item._id || index}
                        style={{
                          padding: "1.5rem",
                          border: "1px solid #e5e7eb",
                          borderRadius: "0.5rem",
                          backgroundColor: "#ffffff",
                        }}
                      >
                        <h3
                          style={{
                            fontSize: "1.25rem",
                            fontWeight: "bold",
                            marginBottom: "0.5rem",
                          }}
                        >
                          {item.name}
                        </h3>
                        {item.description && (
                          <p style={{ color: "#6b7280", fontSize: "0.875rem" }}>
                            {item.description}
                          </p>
                        )}
                      </div>
                    );
                  }
                  return null;
                })}
              </div>
            );
          }
          default:
            return (
              <div style={getContentStyles()} className={componentClassName}>
                {dynamicSnippetData.map((item, index) => (
                  <div key={index}>{JSON.stringify(item)}</div>
                ))}
              </div>
            );
        }
      }
      default:
        return (
          <div
            style={getContentStyles()}
            {...(componentClassName && { className: componentClassName })}
          >
            Unknown component: {component.type}
          </div>
        );
    }
  };

  const handleEditClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    // Always use the currently selected component (which should be the icon if clicked)
    // If no component is selected, use this component
    if (selectedComponent) {
      // Ensure the selected component is set (in case it wasn't already)
      setSelectedComponent(selectedComponent);
    } else {
      // Fallback: select this component
      setSelectedComponent(component);
    }
    // Then trigger drawer to open via custom event
    window.dispatchEvent(new CustomEvent("pagebuilder:openDrawer"));
  };

  const positioningStyles = getPositioningStyles();
  const hasPositioning = Object.keys(positioningStyles).length > 0;
  const positionType = component.styles?.default?.position || "static";

  // Only make wrapper relative if component is relative (creates positioning context for absolute children)
  // For absolute/fixed/sticky, positioning styles are applied to wrapper, but wrapper is NOT relative
  // For static, only make relative in editor mode for UI purposes
  const wrapperRelative =
    positionType === "relative" ||
    (positionType === "static" && !isPreviewMode);

  // Check if dynamic snippet has content - if so, don't show editor styling
  const isDynamicSnippetWithContent =
    component.type === "dynamic-snippet" &&
    component.props?.dataSource &&
    dynamicSnippetData.length > 0 &&
    !loadingDynamicSnippet;

  return (
    <div
      ref={setNodeRef}
      style={{
        ...style,
        // Apply positioning styles to wrapper (position, top, right, bottom, left, zIndex)
        ...(hasPositioning ? positioningStyles : {}),
        // Only add relative to wrapper if component is relative, or static in editor mode
        ...(wrapperRelative && !hasPositioning
          ? { position: "relative" as const }
          : {}),
      }}
      {...(!isPreviewMode ? attributes : {})}
      {...(!isPreviewMode ? modifiedListeners : {})}
      onClick={(e) => {
        // Don't select if clicking on a child component that should handle its own selection
        const target = e.target as HTMLElement;
        // If clicking on an icon or its parent span, let the icon handle selection
        const iconElement = target.closest("span[data-icon-component]");
        if (iconElement) {
          // Icon will handle its own selection, don't select container
          e.stopPropagation();
          e.preventDefault();
          e.nativeEvent.stopImmediatePropagation();
          return;
        }
        // Also check if clicking on SVG (icon component)
        if (
          target.closest("svg") &&
          target.closest("span[data-icon-component]")
        ) {
          e.stopPropagation();
          e.preventDefault();
          e.nativeEvent.stopImmediatePropagation();
          return;
        }
        // Allow selecting container even if it has children - user can select any layer
        handleClick(e);
      }}
      onDoubleClick={(e) => {
        // Don't open drawer if double-clicking on a child component
        const target = e.target as HTMLElement;
        const iconElement = target.closest("span[data-icon-component]");
        if (iconElement) {
          e.stopPropagation();
          e.preventDefault();
          return;
        }
        if (
          target.closest("svg") &&
          target.closest("span[data-icon-component]")
        ) {
          e.stopPropagation();
          e.preventDefault();
          return;
        }
        handleDoubleClick(e);
      }}
      onMouseDown={(e) => {
        // Also prevent container selection on mousedown if clicking icon
        const target = e.target as HTMLElement;
        if (target.closest("span[data-icon-component]")) {
          e.stopPropagation();
          e.preventDefault();
          return;
        }
        // Check if clicking in editable content area
        const isEditableContent =
          target.closest(".ql-editor") ||
          target.closest('[contenteditable="true"]') ||
          target.closest("input") ||
          target.closest("textarea") ||
          target.closest(".select-text");
        // Only prevent default (text selection) if NOT in editable content
        if (!isPreviewMode && !isEditableContent) {
          // Don't prevent if user is trying to select text in an editable area
          // But prevent if they're trying to drag the component
          const isTextSelection = e.detail === 1 && !isDragging;
          if (!isTextSelection) {
            e.preventDefault();
          }
        }
      }}
      onPointerDown={(e) => {
        // Prevent drag from starting if clicking on icon
        const target = e.target as HTMLElement;
        if (target.closest("span[data-icon-component]")) {
          e.stopPropagation();
          return;
        }
        // Check if clicking in editable content area
        const isEditableContent =
          target.closest(".ql-editor") ||
          target.closest('[contenteditable="true"]') ||
          target.closest("input") ||
          target.closest("textarea") ||
          target.closest(".select-text");
        // Only prevent default if NOT in editable content and we're starting a drag
        if (!isPreviewMode && !isEditableContent) {
          // Allow the drag to start, but prevent text selection
          // The drag library will handle the rest
        }
      }}
      onDragStart={() => {
        // Prevent text selection during drag
        if (window.getSelection) {
          window.getSelection()?.removeAllRanges();
        }
      }}
      onMouseEnter={() => !isPreviewMode && setIsHovered(true)}
      onMouseLeave={(e) => {
        // Don't hide if mouse is moving to the action buttons
        const target = e.relatedTarget;
        if (
          !target ||
          !(target instanceof HTMLElement) ||
          !target.closest(".absolute.top-0.right-0")
        ) {
          setIsHovered(false);
        }
      }}
      className={(() => {
        const finalClassName = `group transition-all duration-200 ${
          wrapperRelative ? "relative" : ""
        } ${
          isSelected && !isPreviewMode
            ? "ring-2 ring-blue-500 ring-offset-1"
            : !isPreviewMode && !isDynamicSnippetWithContent
              ? "border border-dashed border-gray-200 hover:border-gray-400"
              : ""
        } ${
          !isPreviewMode && !isDragging
            ? "cursor-grab active:cursor-grabbing"
            : ""
        } ${
          isHovered &&
          !isPreviewMode &&
          !isSelected &&
          !isDynamicSnippetWithContent
            ? " bg-gray-50/30"
            : ""
        } ${
          isDragging ? "opacity-50" : ""
        } ${!isPreviewMode ? "select-none" : ""}`;
        return finalClassName;
      })()}
    >
      {/* Component Label */}
      {!isPreviewMode && (
        <div
          className={`absolute top-0 left-0 z-40 px-2 py-1 text-xs font-medium transition-all ${
            isSelected
              ? "bg-blue-500 text-white font-bold "
              : "bg-gray-200 text-gray-600 opacity-70"
          } rounded-br`}
        >
          {getComponentLabel()}
        </div>
      )}

      {!isPreviewMode && isSelected && (
        <div
          className="absolute top-0 right-0 z-50 flex gap-1 pointer-events-none"
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleEditClick(e);
            }}
            onMouseDown={(e) => {
              e.stopPropagation();
              e.preventDefault();
            }}
            onMouseUp={(e) => {
              e.stopPropagation();
              e.preventDefault();
            }}
            className="bg-blue-500 text-white p-1.5 rounded hover:bg-blue-600 transition-colors pointer-events-auto z-50"
            title="Edit component"
          >
            <FaEdit className="text-xs" />
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleDelete(e);
            }}
            onMouseDown={(e) => {
              e.stopPropagation();
              e.preventDefault();
            }}
            onMouseUp={(e) => {
              e.stopPropagation();
              e.preventDefault();
            }}
            className="bg-red-500 text-white p-1.5 rounded hover:bg-red-600 transition-colors pointer-events-auto z-50"
            title="Delete component"
          >
            <FaTrash className="text-xs" />
          </button>
        </div>
      )}
      {renderComponentContent()}
      {!isPreviewMode && (
        <style>{`
     /* Allow text selection in editable content areas */
     .group .ql-editor,
     .group [contenteditable="true"],
     .group .select-text,
     .group input,
     .group textarea {
      user-select: text !important;
      -webkit-user-select: text !important;
      -moz-user-select: text !important;
      -ms-user-select: text !important;
     }
    `}</style>
      )}
    </div>
  );
};
